/**
 * Contact Management System - Flat DTOs
 * Based on contact-api-integration-guide.md (section 6).
 *
 * Flat multilingual mechanism:
 * - When the Client calls the API with languageCode, the name/position/area_covered/bio fields
 *   automatically carry the content translated into the requested language.
 * - The language_code field indicates the language currently applied.
 * - The *_translations_vi / *_translations_en / *_translations_zh fields are always included
 *   so the Frontend can display multiple languages without calling the API again.
 */

export type ContactLanguageCode = 'vi' | 'en' | 'zh';

/** Contact inquiry processing status. */
export type ContactInquiryStatus = 'new' | 'in_progress' | 'resolved' | 'closed';

/** Department / Contact tab (Public response). */
export interface ContactDepartment {
  id: string;
  code: string;
  /** Name translated according to the requested languageCode. */
  name: string;
  /** Description translated according to the requested languageCode. */
  description: string | null;
  /** Language currently applied (e.g. 'vi', 'en', 'zh'). */
  language_code: ContactLanguageCode;

  name_translations_vi: string;
  name_translations_en: string;
  name_translations_zh: string;
  description_translations_vi: string | null;
  description_translations_en: string | null;
  description_translations_zh: string | null;

  icon: string | null;
  image: string | null;
  sort_order: number;
  /** Number of persons belonging to the department (only available on the public endpoint). */
  total_persons?: number;
  is_active: boolean;
  created_at: string;
}

/** Contact person (Public response - Flat DTO). */
export interface ContactPerson {
  id: string;
  department_id: string;
  department_name: string;
  department_code: string;

  /** Full name (not translated - kept as-is). */
  full_name: string;
  /** Position translated according to languageCode. */
  position: string;
  /** Area covered translated according to languageCode. */
  area_covered: string;
  /** Bio translated according to languageCode. */
  bio: string | null;
  language_code: ContactLanguageCode;

  position_translations_vi: string;
  position_translations_en: string;
  position_translations_zh: string;
  area_covered_translations_vi: string;
  area_covered_translations_en: string;
  area_covered_translations_zh: string;
  bio_translations_vi?: string | null;
  bio_translations_en?: string | null;
  bio_translations_zh?: string | null;

  avatar: string | null;
  email: string | null;
  phone: string | null;
  direct_phone: string | null;
  languages: string | null;
  zalo: string | null;

  sort_order: number;
  is_highlight: boolean;
  is_active: boolean;
  created_at?: string;
}

/** Sieve paginated result for the person list. */
export interface ContactPersonCollection {
  collection: ContactPerson[];
  total: number;
  pagesize: number;
  pageIndex: number;
}

/** Payload for submitting the contact form (POST /api/contact/inquiry). */
export interface ContactInquiryRequest {
  full_name: string;
  email: string;
  phone: string;
  company_name?: string | null;
  department_id?: string | null;
  person_id?: string | null;
  subject: string;
  message: string;
}

/** Response returned after submitting an inquiry. */
export interface ContactInquiryResponse {
  success: boolean;
  message?: string;
  data: {
    id: string;
  };
}

/**
 * My Inquiry item (section 6 - GET /api/contact/my-inquiries).
 * Returned by the backend when a logged-in customer looks up their own
 * submitted contact requests. The backend auto-filters by the user's
 * email/phone so all fields below belong to the current user.
 */
export interface MyInquiry {
  id: string;
  department_id: string | null;
  department_name: string | null;
  department_code: string | null;
  person_id: string | null;
  person_name: string | null;
  person_position: string | null;
  full_name: string;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  subject: string;
  message: string;
  status: ContactInquiryStatus;
  admin_notes: string | null;
  ip_address?: string | null;
  created_at: string;
  resolved_at: string | null;
  resolved_by_name: string | null;
}

/** Sieve paginated result for the my-inquiries list. */
export interface MyInquiryCollection {
  collection: MyInquiry[];
  total: number;
  pagesize: number;
  pageIndex: number;
}

/** Generic wrapper response for the public API. */
export interface ContactApiResponse<T> {
  success: boolean;
  data: T;
}

/** Sieve pagination/filter parameters used for list endpoints. */
export interface ContactSieveParams {
  filters?: string;
  sorts?: string;
  page?: number;
  pageSize?: number;
  languageCode?: ContactLanguageCode;
}

// ============================================================================
// Admin CMS DTOs (section 5 - contact-api-integration-guide.md)
// ============================================================================

/** Department - admin form (full flat translations, no total_persons). */
export interface AdminContactDepartment {
  id: string;
  code: string;
  /** Default name (usually = name_translations_vi). */
  name: string;
  description: string | null;
  name_translations_vi: string;
  name_translations_en: string;
  name_translations_zh: string;
  description_translations_vi: string | null;
  description_translations_en: string | null;
  description_translations_zh: string | null;
  icon: string | null;
  image: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  modified_at?: string | null;
}

/** Person - admin form (full flat translations). */
export interface AdminContactPerson {
  id: string;
  department_id: string;
  department_name?: string;
  department_code?: string;
  full_name: string;
  position: string;
  area_covered: string;
  bio: string | null;
  position_translations_vi: string;
  position_translations_en: string;
  position_translations_zh: string;
  area_covered_translations_vi: string;
  area_covered_translations_en: string;
  area_covered_translations_zh: string;
  bio_translations_vi?: string | null;
  bio_translations_en?: string | null;
  bio_translations_zh?: string | null;
  avatar: string | null;
  email: string | null;
  phone: string | null;
  direct_phone: string | null;
  languages: string | null;
  zalo: string | null;
  sort_order: number;
  is_highlight: boolean;
  is_active: boolean;
  created_at: string;
  modified_at?: string | null;
}

/** Contact inquiry - admin form. */
export interface AdminContactInquiry {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  subject: string;
  message: string;
  status: ContactInquiryStatus;
  admin_notes?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  department_code?: string | null;
  person_id?: string | null;
  person_name?: string | null;
  created_at: string;
  modified_at?: string | null;
}

/** Generic Sieve paginated result for admin. */
export interface ContactAdminCollection<T> {
  collection: T[];
  total: number;
  pagesize?: number;
  pageIndex?: number;
}

/** Generic response for the admin API. */
export interface ContactAdminResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

/** Response action (create/update/delete). */
export interface ContactAdminActionResponse {
  success: boolean;
  message?: string;
  data?: {
    id?: string;
  };
}

// ----- Request DTOs (Flat - per guide section 5.1.C / 5.2.C) -----

/** POST/PUT /api/contactDepartments - Flat Request Body. */
export interface ContactDepartmentRequestDTO {
  code: string;
  name: string;
  name_translations_vi: string;
  name_translations_en: string;
  name_translations_zh: string;
  description?: string | null;
  description_translations_vi?: string | null;
  description_translations_en?: string | null;
  description_translations_zh?: string | null;
  icon?: string | null;
  image?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

/** POST/PUT /api/contactPersons - Flat Request Body. */
export interface ContactPersonRequestDTO {
  department_id: string;
  full_name: string;
  position?: string;
  position_translations_vi?: string;
  position_translations_en?: string;
  position_translations_zh?: string;
  area_covered?: string;
  area_covered_translations_vi?: string;
  area_covered_translations_en?: string;
  area_covered_translations_zh?: string;
  bio?: string | null;
  bio_translations_vi?: string | null;
  bio_translations_en?: string | null;
  bio_translations_zh?: string | null;
  avatar?: string | null;
  email?: string | null;
  phone?: string | null;
  direct_phone?: string | null;
  languages?: string | null;
  zalo?: string | null;
  sort_order?: number;
  is_highlight?: boolean;
  is_active?: boolean;
}

/** PUT /api/contactInquiries/{id}/status. */
export interface ContactInquiryStatusUpdateRequest {
  status: ContactInquiryStatus;
  admin_notes?: string | null;
}

// ----- Dialog data for admin forms -----

export interface ContactDepartmentFormDialogData {
  mode: 'create' | 'edit';
  department?: AdminContactDepartment;
}

export interface ContactPersonFormDialogData {
  mode: 'create' | 'edit';
  person?: AdminContactPerson;
  departments: AdminContactDepartment[];
}

export interface ContactInquiryDetailDialogData {
  inquiry: AdminContactInquiry;
}

/** Display label for each inquiry status. */
export const CONTACT_INQUIRY_STATUS_LABELS: { [key in ContactInquiryStatus]: string } = {
  new: 'Mới',
  in_progress: 'Đang xử lý',
  resolved: 'Đã giải quyết',
  closed: 'Đã đóng'
};

/** Badge color for each inquiry status. */
export const CONTACT_INQUIRY_STATUS_CLASSES: { [key in ContactInquiryStatus]: string } = {
  new: 'status-new',
  in_progress: 'status-in-progress',
  resolved: 'status-resolved',
  closed: 'status-closed'
};

